import { useState, useEffect } from "react";
import type { ConfigResponse } from "@shared/api";
import { useLocale } from "@/context/LocaleContext";

export default function FloatingWhatsApp() {
  const [phone, setPhone] = useState<string | null>(null);
  const { t } = useLocale();

  // Definisci il numero di emergenza/fallback una sola volta (solo cifre, con prefisso 39)
  const FALLBACK_PHONE = "393505933542";

  useEffect(() => {
    (async () => {
      try {
        const { fetchConfig } = await import("@/lib/fetchConfig");
        const d: ConfigResponse | null = await fetchConfig().catch(() => null);

        if (
          typeof d?.whatsappPhone === "string" &&
          d.whatsappPhone.length > 0
        ) {
          // Pulizia e impostazione del numero dall'API
          setPhone(d.whatsappPhone.replace(/[^0-9]/g, ""));
        } else {
          // Fallback se l'API non ha il numero
          setPhone(FALLBACK_PHONE);
        }
        return;
      } catch (err: any) {
        console.warn(
          "FloatingWhatsApp: unexpected error fetching config",
          err?.message || err,
        );
        // Fallback di emergenza in caso di errore di fetching
        setPhone(FALLBACK_PHONE);
      }
    })();
  }, []);

  // 1. Pulizia del numero (mantenuta per sicurezza)
  const cleanPhone = phone ? phone.replace(/[^0-9]/g, "") : "";

  // ⭐️ Messaggio pre-compilato dalla localizzazione
  const message = t("whatsapp.message");
  const encodedMessage = encodeURIComponent(message);

  // 2. Costruisce il numero finale e l'URL
  const finalPhone = cleanPhone || FALLBACK_PHONE;

  // Il link finale usa ?text= (con testo vuoto) per forzare l'apertura diretta della chat.
  const href = `https://wa.me/${finalPhone}?text=${encodedMessage}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={t("whatsapp.contact")}
      title={t("whatsapp.contact")}
      className="fixed bottom-4 right-4 z-50 inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 p-0 bg-transparent rounded-full border-[3px] border-white shadow-sm hover:scale-105 transition-transform"
    >
      <svg
        viewBox="0 0 24 24"
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <path
          fill="#25D366"
          d="M12 0C5.373 0 0 5.373 0 12c0 2.122.559 4.104 1.53 5.838L0 24l6.306-1.533A11.954 11.954 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"
        />
        <path
          fill="#ffffff"
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.149-.672.149-.198.297-.767.967-.941 1.165-.173.198-.347.223-.644.074-1.758-.867-3.07-1.532-4.296-3.329-.322-.514.322-.479.927-1.628.099-.198.05-.371-.025-.52-.074-.149-.672-1.612-.923-2.206-.243-.579-.49-.5-.672-.51l-.573-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487 2.98 1.289 2.98.861 3.513.808.537-.053 1.758-.719 2.006-1.413.248-.695.248-1.289.173-1.413-.074-.124-.273-.198-.57-.347z"
        />
      </svg>
    </a>
  );
}
